// RigSentinel Context Engine — Phase RCE-1
//
// PROJECT PROFILE
// ---------------
// A Project Profile groups together the company-level identity that flows
// into every report / kill sheet / mud program (operator, client, country,
// field, block, contractor). Project Profile is the canonical replacement
// for the loose `operator` / `client` strings that used to live on every
// Well Profile.
//
// Storage key: `rsc.project.v1`. Pure CRUD; no derivations. The Migration
// Wizard (lib/rce/migration.js) is the ONLY supported way to populate
// Project Profiles from legacy strings.

import { emit, RCE_EVENTS } from "./events";
import { scoped } from "@/lib/accountScope";

export const PROJECT_SCHEMA_VERSION = 1;
export const PROJECT_STORAGE_KEY    = "rsc.project.v1";

const _now = () => new Date().toISOString();
const _genId = () =>
  `proj_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const _safeParse = (s, fallback) => {
  try { return JSON.parse(s) ?? fallback; } catch { return fallback; }
};

export function emptyProjectProfile() {
  return {
    id: "",
    schemaVersion: PROJECT_SCHEMA_VERSION,
    name: "",          // human label, e.g. "North Sea Alpha — Acme Energy"
    operator: "",
    client: "",
    country: "",
    field: "",
    block: "",
    contractor: "",
    notes: "",
    isActive: true,
    createdAt: null,
    updatedAt: null,
  };
}

export function validateProjectProfile(rec) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["Project profile is missing."] };
  const errs = [];
  if (!rec.name || !String(rec.name).trim())
    errs.push("Project name is required.");
  if (!rec.operator || !String(rec.operator).trim())
    errs.push("Operator is required for a Project Profile.");
  return { ok: errs.length === 0, errors: errs };
}

function _readAll() {
  return _safeParse(scoped.getItem(PROJECT_STORAGE_KEY), []);
}
function _writeAll(rows) {
  scoped.setItem(PROJECT_STORAGE_KEY, JSON.stringify(rows));
}

export function listProjects({ includeInactive = false } = {}) {
  const all = _readAll();
  return (includeInactive ? all : all.filter((r) => r.isActive !== false))
    .slice()
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
}

export function getProject(id) {
  return _readAll().find((r) => r.id === id) || null;
}

export function createProject(payload) {
  const base = { ...emptyProjectProfile(), ...(payload || {}) };
  base.id = _genId();
  base.createdAt = _now();
  base.updatedAt = base.createdAt;
  base.isActive = true;
  const v = validateProjectProfile(base);
  if (!v.ok) {
    const e = new Error("Project profile failed validation.");
    e.validationErrors = v.errors;
    throw e;
  }
  const all = _readAll();
  all.push(base);
  _writeAll(all);
  try { emit(RCE_EVENTS.PROJECT_CHANGED, { id: base.id, kind: "create" }); } catch { /* noop */ }
  return base;
}

export function updateProject(id, patch) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Project profile ${id} not found.`);
  const merged = {
    ...all[i], ...patch,
    id: all[i].id,
    createdAt: all[i].createdAt,
  };
  merged.updatedAt = _now();
  const v = validateProjectProfile(merged);
  if (!v.ok) {
    const e = new Error("Project profile failed validation.");
    e.validationErrors = v.errors;
    throw e;
  }
  all[i] = merged;
  _writeAll(all);
  try { emit(RCE_EVENTS.PROJECT_CHANGED, { id: merged.id, kind: "update" }); } catch { /* noop */ }
  return merged;
}

export function deleteProject(id) {
  const all = _readAll();
  const next = all.filter((r) => r.id !== id);
  _writeAll(next);
  const removed = all.length - next.length;
  if (removed > 0) {
    try { emit(RCE_EVENTS.PROJECT_CHANGED, { id, kind: "delete" }); } catch { /* noop */ }
  }
  return removed;
}

/** Test helper — never called from app code. */
export function _clearAllProjects() {
  scoped.removeItem(PROJECT_STORAGE_KEY);
}
